import React, { ReactNode, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../Icon';
import { Photo } from '../../../../graphql/types';
import VipIcon from '../../../../public/static/images/item/vip.svg';
import PremiumIcon from '../../../../public/static/images/item/feature.svg';
import { urlHashes } from '../../../../utils/helpers/constants';
import { Typography } from '../../../ui/Typography';
import { ShowAllBtn } from '../PhotosGrid/styled';
import {
  CounterText,
  SliderInfoButtonsContainer,
  SliderInfoContainer,
  SliderInfoCounter
} from './styled';

const PhotosGrid = dynamic(
  () => import('../PhotosGrid').then(mod => mod.PhotosGrid),
  { ssr: false }
);

interface SliderInfoProps {
  isVippedShown?: boolean;
  isFeaturedShown?: boolean;
  photos?: Photo[];
  description?: ReactNode;
  pageNumber: number;
  isAuthor: boolean;
  isPublished: boolean;
  itemId: string;
  isReported: boolean;
  isFavorited: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenGallery: (
    e: React.SyntheticEvent<HTMLDivElement>,
    photoNumber?: number
  ) => void;
  setIsAdReported: React.Dispatch<React.SetStateAction<boolean>>;
}

const hasPhotosGridHash = (asPath: string) => {
  try {
    const parsedUrl = new URL(asPath, 'https://bina.az');
    return (
      parsedUrl.hash === `#${urlHashes.photosGrid}` ||
      parsedUrl.hash === `#${urlHashes.fullScreenGallery}`
    );
  } catch {
    return asPath.includes(urlHashes.photosGrid);
  }
};

export const SliderInfo: React.FC<SliderInfoProps> = ({
  isVippedShown,
  isFeaturedShown,
  photos,
  description,
  pageNumber,
  isAuthor,
  isPublished,
  itemId,
  isReported,
  isFavorited,
  setFavorited,
  onOpenGallery,
  setIsAdReported
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [shouldLoadPhotosGrid, setShouldLoadPhotosGrid] = useState(() =>
    hasPhotosGridHash(router.asPath)
  );

  useEffect(() => {
    if (hasPhotosGridHash(router.asPath)) {
      setShouldLoadPhotosGrid(true);
    }
  }, [router.asPath]);

  const counter = `${pageNumber || 1}/${photos?.length}`;

  const openPhotosGrid = () => {
    setShouldLoadPhotosGrid(true);

    const parsedUrl = new URL(router.asPath, window.location.href);

    router.push({
      pathname: parsedUrl.pathname,
      hash: urlHashes.photosGrid,
      search: parsedUrl.search
    });
  };

  return (
    <SliderInfoContainer>
      <SliderInfoButtonsContainer data-cy="cur-item-gallery__info-container-icons">
        {isFeaturedShown && (
          <Icon
            indent={false}
            width={15}
            height={13}
            IconComponent={PremiumIcon}
          />
        )}
        {isVippedShown && (
          <Icon indent={false} width={15} height={13} IconComponent={VipIcon} />
        )}
      </SliderInfoButtonsContainer>

      <SliderInfoCounter data-cy="cur-item-gallery__info-container-counter">
        <CounterText tag="caption_1_new">{counter}</CounterText>
      </SliderInfoCounter>

      {shouldLoadPhotosGrid ? (
        <PhotosGrid
          photos={photos}
          description={description}
          pageNumber={pageNumber}
          isAuthor={isAuthor}
          isPublished={isPublished}
          itemId={itemId}
          isReported={isReported}
          isFavorited={isFavorited}
          setFavorited={setFavorited}
          onOpenGallery={onOpenGallery}
          setIsAdReported={setIsAdReported}
        />
      ) : (
        <ShowAllBtn
          onClick={openPhotosGrid}
          data-cy="show-all-photos"
          data-stat="show-all-photos"
          role="button"
          tabIndex={0}
        >
          <Typography tag="caption_1_new">
            {t('pages.current_item.buttons.all_photos')}
          </Typography>
        </ShowAllBtn>
      )}
    </SliderInfoContainer>
  );
};
